import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AREA_ART, ColorPicker, DIM_ART, L_SHAPE, Num, PickCard, Row } from "./shared";

const CANVAS_BG_OPTIONS = ["#0e1523", "#0b0b0e", "#111827", "#0f172a", "#1c1917", "#f4f4f5"];

export const GeneralTab = ({ d, setPart }) => (
  <div
    className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5"
    data-testid="drawing-general"
  >
    <Row label="Studio canvas background" hint="Colour behind the drawing in the Studio">
      <ColorPicker
        testId="general-canvas-bg"
        value={d.general.canvas_bg || "#0e1523"}
        options={CANVAS_BG_OPTIONS}
        onChange={(v) => setPart("general", { canvas_bg: v })}
      />
    </Row>
    <Row label="Default countertop depth" hint="New runs start at this depth">
      <Num
        testId="general-depth"
        value={d.general.default_depth_in}
        onChange={(v) => setPart("general", { default_depth_in: v })}
        suffix="in"
      />
    </Row>
    <Row label="Default slab size" hint="Used for new sheets on the layout">
      <span className="flex items-center gap-2">
        <Num
          testId="general-slab-length"
          value={d.general.slab_length_in}
          onChange={(v) => setPart("general", { slab_length_in: v })}
        />
        <span className="text-xs text-zinc-500">x</span>
        <Num
          testId="general-slab-width"
          value={d.general.slab_width_in}
          onChange={(v) => setPart("general", { slab_width_in: v })}
          suffix="in"
        />
      </span>
    </Row>
    <Row label="Snap step" hint="How far pieces jump when you drag them">
      <Num
        testId="general-snap"
        value={d.general.snap_in}
        onChange={(v) => setPart("general", { snap_in: v })}
        suffix="in"
      />
    </Row>
    <Row label="Show dimensions by default">
      <Switch
        data-testid="general-show-dims"
        checked={!!d.general.show_dims}
        onCheckedChange={(v) => setPart("general", { show_dims: v })}
      />
    </Row>
    <Row label="Show grid by default">
      <Switch
        data-testid="general-show-grid"
        checked={!!d.general.show_grid}
        onCheckedChange={(v) => setPart("general", { show_grid: v })}
      />
    </Row>
    <Row
      label="Measurement system"
      hint="Quote quantities stay Square Feet (SF) for area and Linear Feet (LF) for length. Applies to drawings only."
    >
      <Select
        value={d.general.measurement_system || "imperial"}
        onValueChange={(v) => setPart("general", { measurement_system: v })}
      >
        <SelectTrigger
          data-testid="general-measurement-system"
          className="h-11 w-48 border-zinc-800 bg-zinc-950 text-sm"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="imperial">Imperial (inches)</SelectItem>
          <SelectItem value="metric">Metric (mm)</SelectItem>
        </SelectContent>
      </Select>
    </Row>
    <Row
      label="Use fractional inch measurements"
      hint="Show dimensions like 12 3/4 or 1 11/16 instead of decimals. You can always type 25 1/2, 25-1/2, 1/2 or 25.5 into any measurement box."
    >
      <Switch
        data-testid="general-fractional"
        checked={d.general.fractional_inches !== false}
        onCheckedChange={(v) => setPart("general", { fractional_inches: v })}
      />
    </Row>
    {d.general.fractional_inches !== false && (
      <Row
        label="Fraction precision"
        hint="Dimensions snap to the nearest 1/8″ or 1/16″ on drawings, in the input boxes and on PDFs. Stored values keep their full decimal."
      >
        <Select
          value={String(Number(d.general.fraction_precision) === 8 ? 8 : 16)}
          onValueChange={(v) => setPart("general", { fraction_precision: Number(v) })}
        >
          <SelectTrigger
            data-testid="general-fraction-precision"
            className="h-11 w-48 border-zinc-800 bg-zinc-950 text-sm"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="16">Nearest 1/16″</SelectItem>
            <SelectItem value="8">Nearest 1/8″</SelectItem>
          </SelectContent>
        </Select>
      </Row>
    )}
    <Row
      label="Search aliases"
      hint="Product and service searches in the drawing also match alternative descriptions."
    >
      <Switch
        data-testid="general-search-aliases"
        checked={!!d.general.search_aliases}
        onCheckedChange={(v) => setPart("general", { search_aliases: v })}
      />
    </Row>
    <Row
      label="Enable edge-drag resize"
      hint="Let staff resize a shape by clicking a side and typing its length. Turn off to lock measured sizes so they can't be changed on the sheet."
    >
      <Switch
        data-testid="general-enable-resize"
        checked={d.general.enable_resize !== false}
        onCheckedChange={(v) => setPart("general", { enable_resize: v })}
      />
    </Row>
    <Row
      label="Seam list on sign-off & install packet"
      hint="List every seam with its length on the customer's drawing sign-off (portal) and the installer's packet. Seams come from the saved studio drawing."
    >
      <Switch
        data-testid="general-show-seams"
        checked={d.general.show_seams === true}
        onCheckedChange={(v) => setPart("general", { show_seams: v })}
      />
    </Row>
    <div
      className="mt-6 border-t border-zinc-800 pt-5"
      data-testid="general-outlet-height"
    >
      <div className="text-sm font-semibold text-zinc-200">
        Outlet height on tall backsplashes
      </div>
      <p className="mt-1 text-xs text-zinc-500">
        When staff drop an outlet or switch onto an upright backsplash, place it this
        far above the counter. Leave at 0 to centre it on the panel height.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <input
          type="number"
          min="0"
          step="0.5"
          data-testid="general-outlet-height-input"
          value={d.general.outlet_height_in ?? 0}
          onChange={(e) =>
            setPart("general", {
              outlet_height_in: Math.max(0, Number(e.target.value) || 0),
            })
          }
          className="h-10 w-24 rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-white"
        />
        <span className="text-xs text-zinc-500">inches above counter</span>
      </div>
    </div>

    <div className="mt-6 border-t border-zinc-800 pt-5" data-testid="general-cutout-clearance">
      <div className="text-sm font-semibold text-zinc-200">Minimum stone around a cut-out</div>
      <p className="mt-1 text-xs text-zinc-500">
        The Studio flags any sink or cooktop that sits closer than this to a piece edge or
        seam. Quartz tolerates a tighter margin than natural stone.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-4">
        {[
          ["cutout_clearance_stone_in", "general-clearance-stone-input", "Natural stone"],
          ["cutout_clearance_quartz_in", "general-clearance-quartz-input", "Quartz / engineered"],
        ].map(([key, tid, label]) => (
          <label key={key} className="flex items-center gap-2">
            <span className="w-36 text-xs text-zinc-400">{label}</span>
            <input
              type="number"
              min="0"
              step="0.25"
              data-testid={tid}
              value={d.general[key] ?? 3}
              onChange={(e) =>
                setPart("general", { [key]: Math.max(0, Number(e.target.value) || 0) })
              }
              className="h-10 w-24 rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-white"
            />
            <span className="text-xs text-zinc-500">in</span>
          </label>
        ))}
      </div>
    </div>

    <div className="mt-6 border-t border-zinc-800 pt-5" data-testid="general-cooktop-setback">
      <div className="text-sm font-semibold text-zinc-200">Cooktop set back from front</div>
      <p className="mt-1 text-xs text-zinc-500">
        When staff drop a cooktop onto a piece, place its front edge this far back from the
        front line of the counter. Leave at 0 to use the same set-back as sinks.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <input
          type="number"
          min="0"
          step="0.25"
          data-testid="general-cooktop-setback-input"
          value={d.general.cooktop_setback_in ?? 0}
          onChange={(e) =>
            setPart("general", {
              cooktop_setback_in: Math.max(0, Number(e.target.value) || 0),
            })
          }
          className="h-10 w-24 rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-white"
        />
        <span className="text-xs text-zinc-500">inches from the front edge</span>
      </div>
    </div>

    <div
      className="mt-6 border-t border-zinc-800 pt-5"
      data-testid="general-attached-pieces"
    >
      <div className="text-sm font-semibold text-zinc-200">
        Physical Attached Pieces
      </div>
      <p className="mt-1 text-xs text-zinc-500">
        Choose whether auto-generated attached pieces (backsplash, waterfall and
        mitre panels) are drawn as separate pieces beside their side in the drawing
        editor. Quote PDFs and customer drawings always show them as separate pieces
        with their own edge colours.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {[
          [
            "indicators",
            "Indicators only",
            "Show only the coloured edge line and the BS / WF tag.",
          ],
          [
            "physical",
            "Show physical pieces",
            "Also draw each attached piece separately.",
          ],
        ].map(([value, title, sub]) => {
          const on = (d.general.attached_pieces || "indicators") === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={on}
              data-testid={`general-attached-${value}`}
              onClick={() => setPart("general", { attached_pieces: value })}
              className={`rounded-xl border p-4 text-left transition-colors duration-150 ${
                on
                  ? "border-indigo-400 bg-indigo-500/10"
                  : "border-zinc-800 bg-zinc-950/60 hover:border-zinc-600"
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                    on ? "border-indigo-400" : "border-zinc-600"
                  }`}
                >
                  {on && <span className="h-2 w-2 rounded-full bg-indigo-400" />}
                </span>
                <span className="text-sm font-semibold text-zinc-100">{title}</span>
              </div>
              <p className="mt-1 pl-6 text-xs text-zinc-400">{sub}</p>
              <svg
                viewBox="0 0 280 130"
                className="mt-3 h-28 w-full rounded-md border border-zinc-800 bg-zinc-950"
                aria-hidden
              >
                <g transform={value === "physical" ? "translate(40 45)" : "translate(40 30)"}>
                  <rect x="0" y="0" width="150" height="60" fill="none" stroke="#e4e4e7" strokeWidth="1.5" />
                  <line x1="0" y1="-5" x2="150" y2="-5" stroke="#e4e4e7" strokeWidth="1" />
                  <line x1="157" y1="0" x2="157" y2="60" stroke="#e4e4e7" strokeWidth="1.2" strokeDasharray="3 2" />
                  <text x="60" y="24" fill="#e4e4e7" fontSize="9" fontWeight="600">BS-4"</text>
                  <text x="95" y="40" fill="#e4e4e7" fontSize="9" fontWeight="600">WF-36"</text>
                </g>
                {value === "physical" && (
                  <>
                    <g transform="translate(40 17)">
                      <rect x="0" y="0" width="150" height="16" fill="rgba(96,165,250,0.12)" stroke="#60a5fa" strokeWidth="1.2" strokeDasharray="4 2.5" />
                      <text x="75" y="11" fill="#93c5fd" fontSize="8" textAnchor="middle">BS-4" | #1</text>
                    </g>
                    <g transform="translate(205 45)">
                      <rect x="0" y="0" width="60" height="60" fill="rgba(96,165,250,0.12)" stroke="#60a5fa" strokeWidth="1.2" strokeDasharray="4 2.5" />
                      <text x="30" y="33" fill="#93c5fd" fontSize="7.5" textAnchor="middle">WF-36" | #1</text>
                    </g>
                  </>
                )}
              </svg>
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-zinc-500">
        Individual pieces can still be hidden from their edge settings.
      </p>
    </div>

    <div className="mt-6 border-t border-zinc-800 pt-5">
      <div className="text-sm font-semibold text-zinc-200">
        Quote line aggregation
      </div>
      <p className="mt-1 text-xs text-zinc-500">
        Combine matching lines within each area (e.g. two eased-edge runs in the
        kitchen) into one line. Lines never cross areas — every charge stays under the
        area that used it. Off keeps a separate line per run.
      </p>
      <div className="mt-3 space-y-1" data-testid="general-aggregation">
        {[
          ["materials", "Aggregate materials"],
          ["edges", "Aggregate edges"],
          ["corners", "Aggregate corners"],
          ["cutouts", "Aggregate cut-outs"],
        ].map(([key, label]) => (
          <Row key={key} label={label}>
            <Switch
              data-testid={`aggregation-${key}`}
              checked={!!(d.aggregation && d.aggregation[key])}
              onCheckedChange={(v) => setPart("aggregation", { [key]: v })}
            />
          </Row>
        ))}
      </div>
    </div>

    <div className="mt-6 border-t border-zinc-800 pt-5">
      <div className="text-sm font-semibold text-zinc-200">
        Area calculation mode
      </div>
      <p className="mt-1 text-xs text-zinc-500">
        How the square footage on every quote line is worked out.
      </p>
      <div
        className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
        data-testid="general-area-mode"
      >
        {[
          [
            "actual",
            "Actual area",
            "True geometric area. Includes bump-outs and subtracts bump-ins.",
          ],
          [
            "gross",
            "Gross area",
            "Includes bump-outs but does not subtract bump-ins.",
          ],
          [
            "outline",
            "Rectangular outline",
            "Boxy right-angle outline — every diagonal becomes an L.",
          ],
          [
            "bbox",
            "Bounding box",
            "The smallest rectangle that fits the whole shape.",
          ],
        ].map(([key, label, hint]) => (
          <PickCard
            key={key}
            testId={`area-mode-${key}`}
            active={(d.general.area_mode || "actual") === key}
            label={label}
            hint={hint}
            onClick={() => setPart("general", { area_mode: key })}
          >
            {AREA_ART[key]}
          </PickCard>
        ))}
      </div>
    </div>

    <div className="mt-6 border-t border-zinc-800 pt-5">
      <div className="text-sm font-semibold text-zinc-200">
        Shape measurement display
      </div>
      <p className="mt-1 text-xs text-zinc-500">
        Which side measurements the drawing shows.
      </p>
      <div
        className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
        data-testid="general-dim-display"
      >
        {[
          [
            "main",
            "Main",
            "The main measurement for each side of the shape.",
          ],
          [
            "main_bumps",
            "Main + bumps",
            "Main measurements plus the width and depth of each bump.",
          ],
          [
            "segments",
            "Segments",
            "A separate measurement for every edge of the shape.",
          ],
          [
            "combined",
            "Combined",
            "Main measurements plus extra edge measurements for bumps and corners.",
          ],
        ].map(([key, label, hint]) => (
          <PickCard
            key={key}
            testId={`dim-display-${key}`}
            active={(d.general.measurement_display || "segments") === key}
            label={label}
            hint={hint}
            onClick={() => setPart("general", { measurement_display: key })}
          >
            <path d={L_SHAPE} fill="#6366f1" fillOpacity="0.35" stroke="#a5b4fc" />
            {DIM_ART[key].map((dPath, n) => (
              <path key={n} d={dPath} stroke="#7dd3fc" strokeWidth="1.4" fill="none" />
            ))}
          </PickCard>
        ))}
      </div>
    </div>
  </div>
);
